import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { agentes as initialAgentes, empleados } from '../utils/mockData';
import { Plus, Edit, Trash2, X, AlertCircle, Loader, CircleMinus, CirclePlus} from 'lucide-react';

import { useToast } from "../components/ui/toast-provider";

// Servicios
import { AgenteService } from '../services/agenteService';

// Tipos de Response
import { AgenteResponse } from '../types/agente';

// Modales
import CrearAgente from '../modals/CrearAgente';

import LogoBlanco from '../components/img/LogoBlanco.png';

export function Agentes() {
  const [agentes, setAgentes] = useState<AgenteResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [AgenteEditando, setAgenteEditando] = useState<number>(0);
  const [filtroActivo, setFiltroActivo] = useState(true);
  const { addToast } = useToast();

  // Cargar agentes al montar el componente y cada vez que cambie el filtro
  useEffect(() => {
    cargarAgentes();
  }, [filtroActivo]);

  const cargarAgentes = async () => {
    try {
      setIsLoading(true);
      const res = await AgenteService.getAll(filtroActivo);
      setAgentes(res.data);
    } catch (err) {
      setError("Error al cargar agentes");
    } finally {
      setIsLoading(false);
    }
  };

  const cambiarEstado = async (id: number, activo: boolean) => {
    try {
      setIsLoading(true);
      if (activo) {
        await AgenteService.desactivar(id);
        addToast("Agente desactivado exitosamente");
      } else {
        await AgenteService.activar(id);
        addToast("Agente activado exitosamente");
      }
      await cargarAgentes();
    } catch (err) {
      setError("Error al cambiar estado del agente");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="grid grid-cols-6 md:grid-cols-6 gap-4 p-6 text-white bg-primary border border-primary rounded-t-lg items-center">
        {/* Logo */}
        <div className="col-span-2 md:col-span-1 flex justify-center md:justify-start">
            <img 
            src={LogoBlanco} 
            alt="Logo AGEMI" 
            className="w-20 md:w-40 h-auto object-contain"
            />
        </div>
        <div className="col-span-4 md:col-span-5 flex flex-col text-sm md:text-base">
          <h1 className="text-3xl font-bold">Agentes AGEMI</h1>
        <p className="text-base text-white mt-1 md:mt-2">
          Gestión de los agentes del sistema
        </p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3">
            <Select
              value={filtroActivo ? '1' : '0'}
              onChange={(e) => setFiltroActivo(e.target.value === '1')}
              options={[
                { value: '1', label: 'Agentes Activos' },
                { value: '0', label: 'Agentes Inactivos' },
              ]}
              className="w-full md:w-auto bg-white text-primary"
            />
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-end justify-end mb-4 gap-2">
            {!mostrarFormulario && (
              <Button onClick={() => setMostrarFormulario(true)} variant="accent" className="w-full md:w-auto">
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Agente
              </Button>
            )}
          </div>
          {error && (
            <div className="p-4 mb-4 bg-destructive/10 border border-destructive rounded-lg text-destructive flex items-start gap-2">
              <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold">Error al cargar agentes</p>
                <p className="text-sm">{error}</p>
              </div>
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader className="w-6 h-6 animate-spin text-primary mr-2" />
              <p>Cargando agentes...</p>
            </div>
          ) : agentes.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">No hay agentes para mostrar</p>
            </div>
          ) : (
            <div className="overflow-auto max-h-[400px] border-gray-400 mb-10">
                <table className="min-w-full text-left">
                  <thead className="bg-primary text-white sticky top-0">
                    <tr>
                      <th className="px-3 md:px-4 py-2 md:py-3 text-left font-semibold">Usuario</th>
                      <th className="px-3 md:px-4 py-2 md:py-3 text-left font-semibold">Agente</th>
                      <th className="px-3 md:px-4 py-2 md:py-3 text-left font-semibold">Póliza</th>
                      <th className="px-3 md:px-4 py-2 md:py-3 text-left font-semibold">Negocio</th>
                      <th className="px-3 md:px-4 py-2 md:py-3 text-left font-semibold hidden md:table-cell">Propietario</th>
                      <th className="px-3 md:px-4 py-2 md:py-3 text-left font-semibold hidden lg:table-cell">Municipio</th>
                      <th className="px-3 md:px-4 py-2 md:py-3 text-left font-semibold hidden lg:table-cell">Ubicación</th>
                      <th className="px-3 md:px-4 py-2 md:py-3 text-left font-semibold">Operador</th>
                      <th className="px-3 md:px-4 py-2 md:py-3 text-center font-semibold">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agentes.map((agente) => (
                      <tr key={agente.idAgente} className="border-t border-border hover:bg-muted/30 transition-colors">
                        <td className="px-3 md:px-4 py-2 md:py-3 font-medium text-xs md:text-sm">
                          <div className="truncate">{agente.usuario}</div>
                        </td>
                        <td className="px-3 md:px-4 py-2 md:py-3 text-xs md:text-sm">{agente.agente}</td>
                        <td className="px-3 md:px-4 py-2 md:py-3 text-xs md:text-sm">{agente.noPoliza}</td>
                        <td className="px-3 md:px-4 py-2 md:py-3 text-xs md:text-sm">{agente.nombreNegocio}</td>
                        <td className="px-3 md:px-4 py-2 md:py-3 text-xs md:text-sm hidden md:table-cell">{agente.propietario}</td>
                        <td className="px-3 md:px-4 py-2 md:py-3 text-xs md:text-sm hidden lg:table-cell">{agente.nombreMunicipio}</td>
                        <td className="px-3 md:px-4 py-2 md:py-3 text-xs md:text-sm hidden lg:table-cell">{agente.ubicacion}</td>
                        <td className="px-3 md:px-4 py-2 md:py-3 text-xs md:text-sm">{agente.nombreEmpleado}</td>
                        <td className="px-3 md:px-4 py-2 md:py-3">
                          <div className="flex justify-center gap-1 md:gap-2">
                            <Button
                              size="sm"
                              variant="accent"
                              className="text-xs md:text-sm px-2 md:px-3"
                              onClick={() => {
                                setAgenteEditando(Number(agente.idAgente));
                                setMostrarFormulario(true);
                              }}
                            >
                              <Edit size={16} />
                            </Button>
                            <Button
                              size="sm"
                              variant={filtroActivo ? "destructive" : "primary"}
                              className="text-xs md:text-sm px-2 md:px-3"
                              onClick={() => {
                                cambiarEstado(Number(agente.idAgente), filtroActivo);
                              }}
                            >
                              {filtroActivo ? <CircleMinus size={16} /> : <CirclePlus size={16} />}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
            </div>
          )}
        </CardContent>
      </Card>

      <CrearAgente
        isOpen={mostrarFormulario} 
        onClose={async () => {
          setMostrarFormulario(false);
          setAgenteEditando(0);
        }}
        onGuardado={async() => {
          await cargarAgentes();
          setMostrarFormulario(false);
        }}
        idEditando = {AgenteEditando !== null ? Number(AgenteEditando) : 0}
      />   
    </div>
  );
}
